import {
    CollectionDataLoaded,
    ContentDataTypes,
    DataLoaderGlobalParams,
    useOnScreen,
} from '@news-mono/web-common'
import { MaybeLoaded } from '@west-australian-newspapers/json-react-layouts-data-loader'
import React, { RefObject, useEffect, useRef, useState } from 'react'
import { LoadedData, useInfiniteScroll } from './useInfiniteScroll'
import { StyledInfiniteScrollContainer } from './InfiniteScroll.styled'
import { useMetadata } from '../../SevenNewsV2/collections/SixCardCollection/useMetaData'
import {
    CurrentPageStyled,
    PaginationLinkStyled,
    PaginationLinksWrapperStyled,
} from '../../SevenNewsV2/collections/SixCardCollection/SixCardCollection.styled'
import { LoadingEllipsis } from '../../LoadingEllipsis'

export interface InfiniteScrollRouteProps {
    loadMoreEnabled?: boolean
}

interface InfiniteScrollProps {
    dataDefinition: ContentDataTypes
    data: MaybeLoaded<CollectionDataLoaded>
    onLoadMore: (data: LoadedData) => void
    services: DataLoaderGlobalParams
    loadMoreEnabled: boolean
    render: (
        data: MaybeLoaded<CollectionDataLoaded>,
    ) => JSX.Element | JSX.Element[]
    isVisible?: boolean
    ref?: RefObject<HTMLDivElement> | null
    currentPage?: number | null
    setCurrentPage: (v: number | undefined) => void
    currentUrl?: string
    pathname?: string
}

export const InfiniteScroll = ({
    dataDefinition: initialDataDefinition,
    data: initialData,
    onLoadMore,
    services,
    loadMoreEnabled,
    render,
    currentPage,
    setCurrentPage,
    currentUrl,
    pathname,
}: InfiniteScrollProps) => {
    const { loadMore, isLoading, data, dataDefinition } = useInfiniteScroll(
        initialDataDefinition,
        initialData,
        services,
        onLoadMore,
    )
    const isLocalEnv = currentUrl?.includes('localhost')
    const http = isLocalEnv ? 'http' : 'https'
    const ref = useRef<HTMLDivElement>(null)

    const isInViewport = useOnScreen(ref)

    const prevLink =
        (currentPage as number) > 1
            ? `${http}://${currentUrl}${pathname}?page=${
                  (currentPage as number) - 1
              }`
            : null

    const nextLink = `${http}://${currentUrl}${pathname}?page=${
        (currentPage as number) + 1
    }`

    const canonicalUrl = `${http}://${currentUrl}${pathname}?page=${currentPage}`

    useEffect(() => {
        if (
            isInViewport &&
            loadMoreEnabled &&
            data.loaded &&
            data.result.loadMorePossible &&
            dataDefinition &&
            dataDefinition.type === 'listing' &&
            dataDefinition.paging &&
            !isLoading
        ) {
            loadMore(data)
        }
    }, [
        isInViewport,
        data,
        loadMore,
        dataDefinition,
        loadMoreEnabled,
        isLoading,
        setCurrentPage,
        currentUrl,
    ])

    function infiniteScrollArea() {
        if (
            loadMoreEnabled &&
            data.loaded &&
            data.result.loadMorePossible &&
            dataDefinition &&
            dataDefinition.type === 'listing' &&
            dataDefinition.paging
        ) {
            return (
                <>
                    <StyledInfiniteScrollContainer ref={ref}>
                        {isLoading ? <LoadingEllipsis color="grey" /> : null}
                    </StyledInfiniteScrollContainer>
                    <noscript>
                        <PaginationLinksWrapperStyled>
                            {prevLink && (
                                <PaginationLinkStyled
                                    href={prevLink}
                                    onClick={(e) => {
                                        e.preventDefault()
                                    }}
                                >
                                    Previous
                                </PaginationLinkStyled>
                            )}
                            <CurrentPageStyled>{currentPage}</CurrentPageStyled>
                            {nextLink && (
                                <PaginationLinkStyled
                                    href={nextLink}
                                    onClick={(e) => {
                                        e.preventDefault()
                                    }}
                                >
                                    Next
                                </PaginationLinkStyled>
                            )}
                        </PaginationLinksWrapperStyled>
                    </noscript>
                </>
            )
        }
        return null
    }

    useMetadata({ canonicalUrl, prevLink, nextLink })

    return (
        <>
            {render(data)}
            {infiniteScrollArea()}
        </>
    )
}
